import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, Link } from "react-router-dom";
import { LayoutDashboard, CalendarCheck, Users, Mail, Car, Wrench, MapPinned, Star, FileText, MapPin, Settings, KeyRound, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/messages", label: "Messages", icon: Mail },
  { to: "/admin/vehicles", label: "Fleet", icon: Car },
  { to: "/admin/services", label: "Services", icon: Wrench },
  { to: "/admin/packages", label: "Tour Packages", icon: MapPinned },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/blogs", label: "Blog", icon: FileText },
  { to: "/admin/cities", label: "City SEO", icon: MapPin },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/admin/password", label: "Password", icon: KeyRound },
];

export default function AdminLayout() {
  const { admin, loading, logout } = useAuth();
  const s = useSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-white/10 flex items-center gap-3">
        <img src={s.logo} alt="logo" className="h-10 w-10 rounded-full object-cover" />
        <div><div className="text-white font-heading font-bold text-sm">Chouhan Taxi</div><div className="text-brand-sky text-[10px] uppercase tracking-wider">Admin Panel</div></div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-brand text-white" : "text-slate-300 hover:bg-white/10"}`}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}>
            <n.icon className="w-4.5 h-4.5 w-5 h-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-300 hover:bg-white/10"><ExternalLink className="w-5 h-5" /> View Website</Link>
        <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-300 hover:bg-red-500/10" data-testid="admin-logout-btn"><LogOut className="w-5 h-5" /> Logout</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:block w-64 bg-navy fixed inset-y-0 left-0 z-30">{<Sidebar />}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 inset-y-0 w-64 bg-navy"><Sidebar /></aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3">
          <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu className="w-6 h-6 text-navy" /></button>
          <span className="font-heading font-bold text-navy">Admin Panel</span>
          <button onClick={doLogout}><LogOut className="w-5 h-5 text-red-500" /></button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
